import * as React from 'react';
import {
  Box,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from '@material-ui/core';
import type { FilterOptionsState } from '@material-ui/lab';
import { Skeleton, Autocomplete } from '@material-ui/lab';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { matchSorter } from 'match-sorter';
import type { ILabelValuePair } from '../../../marking/MarkingInterfaces';
import getRuleFields from '../../../../lib/SWRfetchers/getRuleFields';
import getRuleOperations from '../../../../lib/SWRfetchers/getRuleOperations';
import type { IRuleCondition } from '../Utilities/PolicyConfiguratorInterfaces';
import getRuleValues from '../../../../lib/SWRfetchers/getRuleValues';
import getRuleEntityRelationship from '../../../../lib/SWRfetchers/getRuleEntityRelationship';

interface IConditionFilterProps {
  index: number;
  condition: IRuleCondition
  updateCondition: (idx: number, updatedCondition: IRuleCondition) => void;
  handleRemoveCondition: (index: number) => void;
  showCollectionOperators: boolean;
  showErrors: boolean;
}

const collectionOperators = [
  {
    value: 'Any',
    label: 'Any',
  },
  {
    value: 'All',
    label: 'All',
  },
];

const ConditionFilter: React.FC<IConditionFilterProps> = ({
  index,
  condition,
  updateCondition,
  handleRemoveCondition,
  showCollectionOperators,
  showErrors,
}) => {
  const { fields } = getRuleFields();
  const { operations } = getRuleOperations();
  const { values } = getRuleValues(condition.field);
  const { isCollection } = getRuleEntityRelationship(condition.field);

  const filterOptions = (opts: ILabelValuePair[], state: FilterOptionsState<ILabelValuePair>) => matchSorter(opts, state.inputValue, {
    keys: [
      { threshold: matchSorter.rankings.WORD_STARTS_WITH, key: 'label' },
    ],
  });

  const allowArbitraryValues = (condition.operation === '==' || condition.operation === '!=');

  return (
    <Grid container spacing={2}>
      {showCollectionOperators
      && (isCollection ? (
        <Grid item lg={2} md={2} xs={12}>
          <Box py={1.5}>
            <Autocomplete
              value={collectionOperators.find((field) => field.value === condition?.collectionOperator) || { label: '', value: '' }}
              onChange={(event, selectedOption) => {
                if (selectedOption) {
                  const updatedCondition = { ...condition, collectionOperator: selectedOption?.value ?? condition.collectionOperator };
                  updateCondition(index, updatedCondition);
                } else {
                  updateCondition(index, { ...condition, collectionOperator: '' });
                }
              }}
              autoHighlight
              autoSelect
              options={collectionOperators}
            // disabled={!isCollection}
              getOptionLabel={(option) => option?.label?.toString()}
              renderInput={(params) => (
                <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  id={`condition-${index}-collection-operation-select`}
                  label="Collection Operation"
                  variant="outlined"
                  error={!condition?.collectionOperator}
                  helperText={!condition?.collectionOperator ? 'Must Select Collection Operator' : ' '}
                />
              )}
            />
          </Box>
        </Grid>
      ) : (
        <Grid item lg={2} md={2} xs={12} />
      ))}
      <Grid item lg={showCollectionOperators ? 3 : 4} md={showCollectionOperators ? 3 : 4} xs={12}>
        <Box pl={1} py={1.5}>
          {!fields
            ? <Skeleton variant="rect" height={79} />
            : (
              <Autocomplete
                value={fields.find((field) => field.value === condition.field) || { label: '', value: '' }}
                onChange={(event, selectedOption) => {
                  if (selectedOption) {
                    updateCondition(index, { ...condition, field: selectedOption?.value, collectionOperator: undefined });
                  } else {
                    updateCondition(index, {
                      field: '', operation: '', value: '', collectionOperator: undefined,
                    });
                  }
                }}
                autoHighlight
                options={fields}
                getOptionLabel={(option) => option?.label?.toString()}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    id={`condition-${index}-field-select`}
                    label="Field"
                    autoFocus={index !== 0}
                    variant="outlined"
                    error={showErrors && !condition.field}
                    helperText={showErrors && !condition.field ? 'Select Field' : ' '}
                  />
                )}
              />
            )}
        </Box>
      </Grid>
      <Grid item lg={showCollectionOperators ? 2 : 3} md={showCollectionOperators ? 2 : 3} xs={12}>
        <Box py={1.5}>
          {!operations
            ? <Skeleton variant="rect" height={79} />
            : (
              <Autocomplete
                value={operations.find((field) => field.value === condition.operation) || { label: '', value: '' }}
                onChange={(event, selectedOption) => {
                  if (selectedOption) {
                    const updatedCondition = { ...condition, operation: selectedOption.value, value: selectedOption?.value !== '==' ? '' : condition.value };
                    updateCondition(index, updatedCondition);
                  } else {
                    updateCondition(index, { ...condition, operation: '' });
                  }
                }}
                autoHighlight
                autoSelect
                options={operations}
                getOptionLabel={(option) => option?.label?.toString()}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    id={`condition-${index}-field-select`}
                    label="Operation"
                    variant="outlined"
                    error={showErrors && !condition?.operation}
                    helperText={showErrors && !condition?.operation ? 'Select Operation' : ' '}
                  />
                )}
              />
            )}
        </Box>
      </Grid>
      <Grid item lg={4} md={4} xs={12}>
        <Box py={1.5}>
          {
          !values
            ? (
              <Tooltip title="Select Field to see values">
                <TextField
                  fullWidth
                  label="Select Field to see values"
                  variant="outlined"
                  error={showErrors && !condition?.value}
                  helperText={showErrors && !condition?.value ? 'Select Value' : ' '}
                  disabled
                />
              </Tooltip>
            )
            : (
              <Autocomplete
                inputValue={condition?.value}
                onInputChange={(event, selectedOption, reason) => {
                  if (reason === 'input' && selectedOption === '') {
                    updateCondition(index, { ...condition, value: '' });
                  } else if (condition.field !== selectedOption && !(reason === 'reset' && selectedOption === '')) {
                    updateCondition(index, { ...condition, value: selectedOption });
                  }
                }}
                onBlur={() => {
                  if (allowArbitraryValues) {
                    const validOptionCheck = values.findIndex((f) => f.value === condition?.value);
                    if (validOptionCheck === -1) {
                      updateCondition(index, { ...condition, value: '' });
                    }
                  }
                }}
                filterOptions={filterOptions}
                autoHighlight={allowArbitraryValues}
                autoSelect={allowArbitraryValues}
                freeSolo={!allowArbitraryValues}
                options={[...values, { value: '', label: '' }]}
                getOptionLabel={(option) => option?.label?.toString()}
                getOptionSelected={(option, value) => option?.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    id={`condition-${index}-value-select`}
                    label="Value"
                    variant="outlined"
                    error={showErrors && !condition?.value}
                    helperText={showErrors && !condition?.value ? 'Select Value' : ' '}
                  />
                )}
              />
            )
          }
        </Box>
      </Grid>
      <Grid item xs={1}>
        <Box display="flex" p={1.5} mb={2}>
          <Tooltip title="Remove Condition" placement="top">
            <IconButton onClick={() => { handleRemoveCondition(index); }} style={{ color: '#6CAE75', float: 'right' }} disabled={false}>
              <RemoveCircleIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ConditionFilter;
